import React from 'react';
import DefaultLayout from '../../layouts/DefaultLayout';
import { StaticImage } from 'gatsby-plugin-image';
import TemplateColumn, { TemplateColumnWithSidebarContainer } from '../../layouts/TemplateColumn';
import {ComparisonTable, KMCurve, FeaturedStat, PageHeader, PageSection, RelatedContent, CrossLink, IndicationPageIntro, AnchorLinks, PrimaryClaim, NCCNcallout, PageSubSection, SecondaryCallout, TextBlock, PatientProfile, ReferencesBlock, ForestPlot } from '../../components';
import ENDO from '../../components/StudyDesign/__study-design/advanced-or-recurrent-endo';
import { jobCodes } from '../../util/data/jobCodes';

// SEO INFORMATION
const pageMeta = {
    title: `KEYNOTE-868 - Clinical Trial Results & Study Design`,
    keywords: `keynote 868, keynote-868`,
    description: `Health care professionals may find information on the KEYNOTE-868 (NRG-GY018) study, including clinical findings and study design, in patients with primary advanced or recurrent endometrial carcinoma.`,
    schemaJsonLD: [
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/advanced-or-recurrent-endometrial-carcinoma/","@type":"ApprovedIndication","name":"KEYTRUDA and KEYTRUDA QLEX™","alternateName":"pembrolizumab and berahyaluronidase alfa-pmph","description":"KEYTRUDA and KEYTRUDA QLEX™ (pembrolizumab and berahyaluronidase alfa-pmph) Injection 165 mg + 2,000 units/mL are each indicated, in combination with carboplatin and paclitaxel, followed by KEYTRUDA or KEYTRUDA QLEX as a single agent, for the treatment of adult patients with primary advanced or recurrent endometrial carcinoma."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/advanced-or-recurrent-endometrial-carcinoma/","@type":"MedicalAudience","audienceType":"US Health Care Professionals","geographicArea":"This site is intended only for residents of the United States and its territories, including Puerto Rico."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/advanced-or-recurrent-endometrial-carcinoma/","@type":"MedicalWebPage","name":"Advanced Endometrial Carcinoma","description":"Primary Advanced or Recurrent Endometrial Carcinoma"}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/advanced-or-recurrent-endometrial-carcinoma/","@type":"MedicalTrial","name":"Clinical Findings From KEYNOTE⁠-⁠868 at Interim Analysis 1 (IA1)","description":"Study Overview: Designed With Separate pMMR and dMMR Patient Cohorts Phase 3, multicenter, randomized, double-blind, placebo-controlled trial in 810 patients with advanced or recurrent endometrial carcinoma. The study design included two separate cohorts based on MMR status; 222 (27%) patients were in the dMMR cohort and 588 (73%) were in the pMMR cohort. ","trialDesign":"RandomizedTrial"}`
    ]
}

//from /src/util/data/indications.js
const indicationId = 1;
const jobCode = jobCodes[32].jobCode;

const relatedContent = [
    {
        section: 'Resources',
        link: {
            url: '/resources/mechanism-of-action/',
            label: 'Mechanism of Action'
        }
    },
    {
        section: 'Resources',
        link: {
            url: 'https://www.merckaccessprogram-keytruda.com/',
            label: 'Access & Reimbursement Support'
        }
    }
];

const crossLinkContent = {
    sections: [
        // {
        //     heading: "Additional Clinical Data",
        //     items: [
        //         { text: 'Advanced MSI-H/dMMR Endometrial Carcinoma', url: '/efficacy/advanced-endometrial-carcinoma/' },
        //     ]
        // },
        {
            heading: "Related Information",
            items: [
                { text: 'Selected Adverse Reactions', url: '/safety/adverse-reactions/advanced-or-recurrent-endometrial-carcinoma/' },
                { text: 'Dosing', url: '/dosing/options/#endo' },
            ]
        },
    ],
    interestLinks: {
        heading: "You Might Be Interested In",
        items: [
            { eyebrow: 'Resources', text: 'Mechanism of Action', url: '/resources/mechanism-of-action/' },
        ],
    }
};


const anchorLinkModelData = [
    { label: 'Clinical Findings From KEYNOTE&#8288;-&#8288;868 at Interim Analysis 1 (IA1)' },
    { label: 'Study Design for KEYNOTE&#8288;-&#8288;868' },
    { label: 'NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines®) ' },
    { label: 'Appropriate Patients' }
]

const NCCNcalloutData = {
    title: 'National Comprehensive Cancer Network® (NCCN®) recommends pembrolizumab (KEYTRUDA) or pembrolizumab and berahyaluronidase alfa-pmph (KEYTRUDA QLEX) in combination with carboplatin/paclitaxel as treatment for primary (stage III–IV) or recurrent endometrial carcinoma (except for carcinosarcoma).<sup>4,a,b,c</sup>',
    showBadge: false,
};

const NCCNFootnotes = [
    {
        label: "a.",
        text: "For stage III or IVA with measurable disease or stage IVB with or without measurable disease.<sup>4</sup>"
    },
    {
        label: "b.",
        text: "Checkpoint inhibitors and/or monoclonal antibodies included in this regimen may be continued as maintenance therapy. Refer to original study protocol for maintenance therapy dosing schedules.<sup>4</sup>"
    },
    {
        label: "c.",
        text: "Pembrolizumab berahyaluronidase alfa-pmph subcutaneous injection may be substituted for intravenous pembrolizumab. Pembrolizumab berahyaluronidase alfa-pmph has different dosing and administration instructions compared to intravenous pembrolizumab.<sup>4</sup>"
    }
];

const NCCNdefinitions = `
    <p>NCCN makes no warranties of any kind whatsoever regarding their content, use or application and disclaims any responsibility for their application or use in any way.</p>
    <p>Category 1 = Based upon high-level evidence, there is uniform NCCN consensus that the intervention is appropriate.<sup>4</sup> Preferred intervention = Interventions that are based on superior efficacy, safety, and evidence; and, when appropriate, affordability.<sup>4</sup></p>
`;

// const NCCNcalloutReferences = [
//     {
//         label: "4.",
//         text: "Referenced with permission from the NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines®) Uterine Neoplasms V2.2024. National Comprehensive Cancer Network, Inc. 2024. Accessed March 12, 2024. To view the most recent and complete version of the guidelines, go online to NCCN.org."
//     },
// ];


const FeturedStatDefinitions = "HR = hazard ratio; CI = confidence interval."

const PFSComparisonFootnotes = [
     {
        label: "a.",
        text: "Paciltaxel + carboplatin."
    }, 
    {
        label: "b.",
        text: "Based on interim PFS analysis; the information fractions for interim analyses were 49% for dMMR and 55% for pMMR."
    }, 
    {
        label: "c.",
        text: "Based on the stratified Cox proportional hazard model."
    },
    {
        label: "d.",
        text: "Based on the stratified log-rank test."
    },
]

const ComparissonTableDMMRFootnotes = [
    {
        label: "e.",
        text: "Based on interim PFS analysis; the information fractions for interim analyses were 49% for dMMR and 55% for pMMR."
    }, 
    {
        label: "f.",
        text: "Paclitaxel + carboplatin."
    },
    {
        label: "g.",
        text: "Based on the stratified Cox proportional hazard model."
    }, 
    {
        label: "h.",
        text: "Based on the stratified log-rank test."
    }
    
]

const ComparissonTableDMMRData = [
    [
        {
            legendTitle: 'KEYTRUDA + chemotherapy<sup>f</sup> (n=110)',
        },
        {
            legendTitle: 'Placebo + chemotherapy<sup>f</sup> (n=112)',
        }
    ],
    [
        {
            colspan: 2,
            centerText: true,
            hrCiP: 'HR<sup>g</sup>=0.30 (95% CI, 0.19&ndash;0.48); <i>P</i><sup>h</sup><0.0001'
        }
    ],
    [
        {
            label: 'Number (%) of patients with an event',
            dataPoint: '26/110 (24%)',
        },
        {
            label: 'Number (%) of patients with an event',
            dataPoint: '57/112 (51%)',
        }
    ],
    [
        {
            label: 'Median PFS in Months',
            dataPoint: 'NR',
            dataSubPoint: '(95% CI, 30.7–NR)',
        },
        {
            label: 'Median PFS in Months',
            dataPoint: '6.5',
            dataSubPoint: '(95% CI, 6.4–8.7)',
        }
    ],
]

const ComparissonTableDMMR = {
    title: "PFS<sup>e</sup> in the dMMR Population in KEYNOTE&#8288;-&#8288;868",
    data: ComparissonTableDMMRData,
    footnotes: ComparissonTableDMMRFootnotes,
    definitions: "NR = not reached."
};

const patientProfileAaliyah = {
    image: () => (
        <StaticImage layout="fixed" src="../../assets/endo_patient.png" placeholder="TRACED_SVG" alt="" width={100} height={100} />
    ),
    name: "Aaliyah",
    diagnosis: "Recurrent pMMR endometrial carcinoma",
    primaryCharacteristics: [
        {
            key: 'Age',
            value: '55',
            emphasize: false,
        },
        {
            key: 'Past medical history',
            value: 'Grade II Stage IB serous endometrial carcinoma',
            emphasize: false,
        },
        {
            key: 'Surgical history',
            value: 'Total hysterectomy and bilateral salpingo-oophorectomy and pelvic lymph node dissection',
            emphasize: false,
        },
        {
            key: '18-month follow-up PET/CT scan of chest, abdomen and pelvis',
            value: 'A tumor mass in the pelvis with no evidence of distant metastases was seen',
            emphasize: false,
        },
        {
            key: 'Tumor MMR status',
            value: 'pMMR',
            emphasize: false,
        },
        {
            key: 'ECOG PS',
            value: '0',
            emphasize: false,
        },
    ],
    secondaryCharacteristics: []
}

const patientProfileFrancine = {
    image: () => (
        <StaticImage layout="fixed" src="../../assets/endo_Francine.png" placeholder="TRACED_SVG" alt="" width={100} height={100} />
    ),
    name: "FRANCINE",
    diagnosis: "Primary advanced pMMR endometrial carcinoma",
    primaryCharacteristics: [
        {
            key: 'Age',
            value: '67',
            emphasize: false,
        },
        {
            key: 'Computed tomography',
            value: 'Enlarged uterus with thickened, complex endometrium, and prominent para-aortic lymph nodes',
            emphasize: false,
        },
        {
            key: 'Biopsy',
            value: 'Grade 3 endometrioid carcinoma',
            emphasize: false,
        },
        {
            key: 'Initial surgical treatment',
            value: 'Hysterectomy with bilateral salpingo-oophorectomy and surgical staging with gross residual disease confirmed by CT scan',
            emphasize: false,
        },
        {
            key: 'Diagnosis',
            value: 'Stage III, grade 3 endometrioid carcinoma with measurable disease per RECIST v1.1',
            emphasize: false,
        },
        {
            key: 'ECOG PS',
            value: '0',
            emphasize: false,
        },
    ],
    secondaryCharacteristics: []
}

const pageReferences = [
    {
        label: "1.",
        text: "Eskander RN, Sill MW, Beffa L, et al. Pembrolizumab plus chemotherapy in advanced or recurrent endometrial cancer: overall survival and exploratory analyses of the NRG GY018 phase 3 randomized trial. Nat Med. Published online March 5, 2025. doi:10.1038/s41591-025-03566-1"
    },
    {
        label: "2.",
        text: "Data available on request from the Merck National Service Center via email at daprequests@merck.com. Please specify information package US-END-00785."
    },
    {
        label: "3.",
        text: "Data available on request from the Merck National Service Center via email at daprequests@merck.com. Please specify information package US-END-00848."
    },
    {
        label: "4.",
        text: "Referenced with permission from the NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines® ) for Uterine Neoplasms V‍.2‍.2‍026. © National Comprehensive Cancer Network, Inc. 2026. All rights reserved. Accessed January 7, 2026. To view the most recent and complete version of the guidelines, go online to NCCN.org."
    },
]

const abbreviationExpansions= 'FDA = Food and Drug Administration; PD-1 = programmed death receptor-1; IO = immunotherapy; PFS = progression-free survival; pMMR = mismatch repair proficient; dMMR = mismatch repair deficient.'

const primaryCalloutHead = `KEYNOTE&#8288;-&#8288;868: The Only FDA-Approved Anti–&#8288;PD-&#8288;1 IO + Chemotherapy<sup>a</sup> Regimen Studied to Evaluate a Statistically Significant PFS Difference in Separate pMMR and dMMR Patient Cohorts`

const PFSSecondaryCallout = `Superior PFS<sup>b</sup> in Both pMMR and dMMR Cohorts of Adult Patients Randomized to KEYTRUDA + Paclitaxel and Carboplatin Followed by KEYTRUDA Alone vs Placebo + Paclitaxel and Carboplatin Followed by Placebo Alone`

const forestPlotPmmrHead = "Forest Plot of PFS for Select Subgroups in pMMR Participants<sup>3</sup>:"
const forestPlotPmmrCopy = "Based on Investigator Assessment per RECIST 1.1 for Select Subgroups (Merck Preferred Censoring Rule) in pMMR Participants (ITT Population)"
const forestPlotPmmrLimitation = "KEYNOTE&#8288;-&#8288;868 was not powered to detect differences in the treatment effect in these subgroups, and no statistical testing was planned for this analysis; therefore, no conclusions can be drawn. Results from exploratory subgroup analyses should be interpreted with caution because of the modest patient numbers and potential imbalances in baseline characteristics within the subgroups."

const forestPlotFootnote = [{
    label: 'i.',
    text: 'Based on Cox regression model with Efron’s method of tie handling with treatment as a covariate. Subgroup analyses were conducted using an unstratified Cox model, with the exception of the ‘Overall’ row, which utilized the same stratified Cox model as conducted for the primary analysis.'
},
{
    label: 'j.',
    text: 'pMMR “Other” histology includes dedifferentiated/undifferentiated, mixed epithelial, and adenocarcinoma NOS. Database Cutoff Date: 06DEC2022 for pMMR participants.'
}]

const forestPlotDmmrHead = "Forest Plot of PFS for Select Subgroups in dMMR Participants<sup>3</sup>:"
const forestPlotDmmrCopy = "Based on Investigator Assessment per RECIST 1.1 for Select Subgroups (Merck Preferred Censoring Rule) in dMMR Participants (ITT Population)"
const forestPlotDmmrLimitation = "KEYNOTE&#8288;-&#8288;868 was not powered to detect differences in the treatment effect in these subgroups, and no statistical testing was planned for this analysis; therefore, no conclusions can be drawn. Results from exploratory subgroup analyses should be interpreted with caution because of the modest patient numbers and potential imbalances in baseline characteristics within the subgroups."

const forestPlotDmmrFootnote = [{
    label: 'k.',
    text: 'Based on Cox regression model with Efron’s method of tie handling with treatment as a covariate. Subgroup analyses were conducted using an unstratified Cox model, with the exception of the ‘Overall’ row, which utilized the same stratified Cox model as conducted for the primary analysis.'
},
{
    label: 'l.',
    text: 'dMMR “other” histology includes serous, clear cell, dedifferentiated/undifferentiated, mixed epithelial, and adenocarcinoma NOS. Database Cutoff Date: 16DEC2022 1for dMMR participants.'
}]

const Page = ({ location }) => {

    return (
        <DefaultLayout indicationId={indicationId} jobCode={jobCode} pageMeta={pageMeta} location={location}>
            <div data-template-name="template-e">
                {/* Begin Intro Column */}
                <TemplateColumn id="template-e-intro" maxWidth={824}>
                    <PageHeader isBranding title="Clinical Trial Results for KEYTRUDA® (pembrolizumab)" />
                    <PageSection bgColor="white">
                        <IndicationPageIntro indicationId={indicationId} />
                        <AnchorLinks items={anchorLinkModelData} />
                    </PageSection>
                    <PageSection bgColor="gradient">
                        <PrimaryClaim theme="light" shortHead=''>{primaryCalloutHead}</PrimaryClaim>
                    </PageSection>
                </TemplateColumn>
                {/* End Intro Column */}

                <TemplateColumnWithSidebarContainer>
                    {/* Begin Main Column */}
                    <TemplateColumn id="template-e-main">
                        <PageSection bgColor="cloud" title={anchorLinkModelData[0].label}>
                            <SecondaryCallout title={PFSSecondaryCallout} definitions={abbreviationExpansions} ></SecondaryCallout>
                            <PageSubSection title="PFS<sup>b</sup> in the pMMR Population (n=588)">
                                <FeaturedStat
                                    title="Reduction in risk of disease progression or death in patients with pMMR tumors with KEYTRUDA + chemotherapy<sup>a</sup> compared with placebo + chemotherapy<sup>a</sup>"
                                    statistic="40%"
                                    hr="HR<sup>c</sup>=0.60 (95% CI, 0.46&ndash;0.78); <i>P</i><sup>d</sup><0.0001"
                                />
                                <KMCurve
                                    image={() => <StaticImage 
                                    src="../../assets/endo-KN-868_pMMR_Curve-v9.png"
                                    placeholder="none"
                                    alt="Kaplan-Meier Curve for PFS in pMMR Patients With Primary Advanced or Recurrent Endometrial Carcinoma" /> }
                                    title="pMMR Population: Kaplan-Meier Curve for PFS<sup>b</sup>"
                                    hr="HR<sup>c</sup>=0.60 (95% CI, 0.46–0.78); <i>P</i><sup>d</sup><0.0001"
                                />
                                <ComparisonTable
                                    title="PFS<sup>b</sup> in the pMMR Population in KEYNOTE&#8288;-&#8288;868"
                                    footnotes={PFSComparisonFootnotes}
                                    definitions={FeturedStatDefinitions}
                                    data={
                                        [
                                            [
                                                {
                                                    legendTitle: 'KEYTRUDA + chemotherapy<sup>a</sup> (n=294)',
                                                },
                                                {
                                                    legendTitle: 'Placebo + chemotherapy<sup>a</sup> (n=294)',
                                                },
                                            ],
                                            [
                                                {
                                                    colspan: 2,
                                                    centerText: true,
                                                    hrCiP: 'HR<sup>c</sup>=0.60 (95% CI, 0.46–0.78); <i>P</i><sup>d</sup><0.0001'
                                                }
                                            ],
                                            [
                                                {
                                                    label: 'Number (%) of patients with an event',
                                                    dataPoint: '91/294 (31%)',
                                                },
                                                {
                                                    label: 'Number (%) of patients with an event',
                                                    dataPoint: '124/294 (42%)',
                                                },
                                            ],
                                            [
                                                {
                                                    label: 'Median PFS in Months',
                                                    dataPoint: '11.1',
                                                    dataSubPoint: '(95% CI, 8.7–13.5)',
                                                },
                                                {
                                                    label: 'Median PFS in Months',
                                                    dataPoint: '8.5',
                                                    dataSubPoint: '(95% CI, 7.2–8.8)',
                                                },
                                            ],
                                        ]
                                    }
                                />
                            </PageSubSection>
                            <PageSubSection title="PFS<sup>e</sup> in the dMMR Population (n=222)">
                                <FeaturedStat
                                    title="Reduction in risk of disease progression or death in patients with dMMR tumors with KEYTRUDA + chemotherapy<sup>f</sup> compared with placebo + chemotherapy<sup>f</sup>"
                                    statistic="70%"
                                    hr="HR<sup>g</sup>=0.30 (95% CI, 0.19&ndash;0.48); <i>P</i><sup>h</sup><0.0001"
                                />
                                <KMCurve 
                                    image={() => <StaticImage 
                                        src="../../assets/endo-KN-868_dMMR_Curve-v9.png"
                                        placeholder="none"
                                        alt="Kaplan-Meier Curve for PFS in dMMR Patients With Primary Advanced or Recurrent Endometrial Carcinoma" 
                                    /> }
                                    title="dMMR Population: Kaplan-Meier Curve for PFS<sup>e</sup>"
                                    hr="HR<sup>g</sup>=0.30 (95% CI, 0.19&ndash;0.48); <em>P</em><sup>h</sup><0.0001"
                                />
                                <ComparisonTable  { ...ComparissonTableDMMR} />

                                <TextBlock>
                                    <h4>KEYNOTE-868 achieved statistically significant PFS at the first interim analysis (IA1)<sup>1,2</sup></h4>
                                    <ul>
                                        <li>At IA1 when PFS met statistical significance, both the investigators and patients were unblinded</li>
                                        <li>Patients were made aware of the study outcome, and both patients and investigators were informed of patients’ treatment assignments</li>
                                        <li>Patients in the placebo arm were instructed to discontinue placebo infusions, and patients in the KEYTRUDA arm were instructed to continue treatment</li>
                                        <li>Additionally, nearly all (&gt;99%) patients in the control arm discontinued placebo infusions</li>
                                        <li>Patients in the study could have received subsequent immunotherapy outside of the study protocol</li>
                                    </ul>
                                     <p>The KEYNOTE-868 trial and efficacy presentation above includes patients with primary advanced measurable Stage III and Stage IVA, or Stage IVB (with or without measurable disease) and patients with recurrent endometrial cancer (with or without measurable disease).</p>
                                </TextBlock>  

                                <TextBlock definitions="DFS = disease-free survival.">
                                    <h4>Clarification: KEYNOTE-B21 data in the adjuvant treatment setting</h4>
                                    <ul>
                                        <li>KEYNOTE-B21 was a separate randomized, multicenter, double-blind, placebo-controlled trial with a different patient population</li>
                                        <li>In KEYNOTE-B21, the efficacy of KEYTRUDA in combination with carboplatin and paclitaxel, with or without radiation (N=1095) was studied for the adjuvant treatment of patients with newly-diagnosed, high-risk endometrial cancer with no evidence of disease on imaging following curative-intent surgery</li>
                                        <li>The KEYNOTE-B21 trial did not meet the prespecified primary endpoint for investigator-assessed DFS, with an HR of 1.02 (95% CI: 0.79, 1.32)</li>
                                    </ul>
                                </TextBlock>  

                            </PageSubSection >
                            <PageSubSection title='KEYNOTE&#8288;-&#8288;868: PFS by Subgroup'>
                            <ForestPlot
                                title= {forestPlotPmmrHead}
                                footnotes={forestPlotFootnote}
                                limitation={`<p>${forestPlotPmmrCopy}</p>
                                <b>LIMITATIONS: </b>${forestPlotPmmrLimitation}`}
                                image={() => <StaticImage
                                    src="../../assets/subgroup_chart_pmmr.png"
                                    placeholder="blurred"
                                    alt="Forest Plot of PFS for Select Subgroups in pMMR Participants" />
                                }
                            />  
                            <ForestPlot
                                title= {forestPlotDmmrHead}
                                footnotes={forestPlotDmmrFootnote}
                                limitation={`<p>${forestPlotDmmrCopy}</p>
                                <b>LIMITATIONS: </b>${forestPlotDmmrLimitation}`}
                                image={() => <StaticImage
                                    src="../../assets/subgroup_chart_dmmr.png"
                                    placeholder="blurred"
                                    alt="Forest Plot of PFS for Select Subgroups in dMMR Participants" />
                                }
                                definitions="RECIST 1.1 = Response Evaluation Criteria In Solid Tumors v1.1; ITT = intent to treat; ECOG = Eastern Cooperative Oncology Group performance status; NOS = not otherwise specified."
                            />  
                                                                                                             
                        </PageSubSection>
                        </PageSection>
                        <PageSection >
                            <PageSubSection title={anchorLinkModelData[1].label}>
                                <ENDO />
                                <SecondaryCallout title="Some Aggressive Histologies, Like Serous and Clear Cell Carcinomas, Were Included in KEYNOTE&#8288;-&#8288;868."></SecondaryCallout>  
                            </PageSubSection>
                        </PageSection>

                        <PageSection bgColor="cloud" title={anchorLinkModelData[2].label}>
                            <NCCNcallout
                                data={NCCNcalloutData}
                                footnotes={NCCNFootnotes}
                                definitions={NCCNdefinitions}
                                // references={NCCNcalloutReferences}
                            />
                        </PageSection>

                        <PageSection title={anchorLinkModelData[3].label}>
                            <PageSubSection title="Hypothetical Patient With Primary Advanced pMMR Endometrial Carcinoma Based on Those Studied in KEYNOTE&#8288;-&#8288;868">
                                <PatientProfile
                                    {...patientProfileFrancine} 
                                />
                            </PageSubSection>

                            <PageSubSection title="Hypothetical Patient With Recurrent pMMR Endometrial Carcinoma Based on Those Studied in KEYNOTE&#8288;-&#8288;868">
                                <PatientProfile
                                    {...patientProfileAaliyah} 
                                />
                            </PageSubSection>
                        </PageSection>

                    </TemplateColumn>
                    {/* End Main Column */}
                    <TemplateColumn id="template-e-sidebar">
                        <PageSection bgColor="mist">
                            <CrossLink {...crossLinkContent} />
                        </PageSection>
                    </TemplateColumn>
                </TemplateColumnWithSidebarContainer>

                <TemplateColumn id="template-e-secondary">
                    <PageSection bgColor="keytrudaGreen">
                        <RelatedContent items={relatedContent} />
                    </PageSection>
                </TemplateColumn>

                <PageSection bgColor="mist">
                    <ReferencesBlock references={pageReferences} />
                </PageSection>
            </div>
        </DefaultLayout>
    );
};

export default Page;